/**
 * SubmittalReviewModal — the Submittal Review workspace shell. Phase 4
 * (docs/SUBMITTAL_REVIEW_PLAN.md) built the read-only three-pane shell; Phase 5 ("Markup,
 * checklist runtime, and complete-review") makes the package pane annotatable and the checklist
 * strip interactive, and wires "Log Review" through to the existing LogSubmittalReviewModal.
 *
 * Opening convention copied verbatim from QcMarkupModal.jsx: mounted once (zero props) in
 * OutlineView.jsx, self-opens on a CustomEvent, `createPortal` to `document.body`,
 * `position:fixed; inset:0` at the same z-index (750) so nothing layers wrong against QC.
 *
 *   window.dispatchEvent(new CustomEvent('open-submittal-review', { detail: { submittalId, revisionId? } }))
 *
 * Layout (flex column, top to bottom):
 *   top bar   — title (submittalLabel) · round badge/switcher · Log/Edit Review · × close
 *   [read-only / lock-held-elsewhere / posted-round banner as applicable]
 *   flex row  — Package pane (AnnotatablePdfPane, flex:1) ⇹ Drawings pane (collapsible) ⇹
 *               Specs pane (collapsible) ⇹ Checklist strip (interactive on the editable round)
 *
 * Phase 5 editing model:
 *   - Only the LATEST round can ever be edited; a past round (Phase 4's isLatestRound) is always
 *     read-only, no lock needed.
 *   - The latest round's revision row is created LAZILY (mirroring LogSubmittalReviewModal's own
 *     lazy Rev-0 creation) the moment the tool opens on a still-virtual "v1" round — see the
 *     dedicated effect below for why this fires eagerly rather than waiting for a save.
 *   - The shared edit lock (`services/locks.js`, resource_type 'submittal_review', resource id =
 *     the latest round's revision id — Decision D10) is held for the duration; a lock held by
 *     someone else, or a round whose review has already been posted, forces `canEdit` false.
 *
 * Data sources — all through the service layer, never a direct fetch here:
 *   getSubmittal / getSubmittalRevisions / createSubmittalRevision (services/submittals.js)
 *   getSubmittalFiles / getRevisionFiles   (services/submittalFiles.js)
 *   getCurrentDrawingSet                   (services/sheets.js — wraps api/drawing-set.js)
 *   getReviewSpecs                         (services/specSections.js)
 *   getSubmittalChecklistTemplates         (services/submittalChecklists.js)
 *   getSubmittalReviewState / seedSubmittalChecklist / setSubmittalChecklistItemStatus / … (services/submittalReview.js)
 *   getProjectDetails                      (services/projects.js — Log Review's transmittal header)
 *   acquireLock / heartbeatLock / releaseLock (services/locks.js)
 *
 * All colors are `var(--aj-*)` tokens — see docs/DARK_MODE.md.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { getSubmittal, getSubmittalRevisions, submittalLabel, createSubmittalRevision } from '../services/submittals.js'
import { getSubmittalFiles, getRevisionFiles } from '../services/submittalFiles.js'
import { getCurrentDrawingSet } from '../services/sheets.js'
import { getReviewSpecs } from '../services/specSections.js'
import { getSubmittalChecklistTemplates } from '../services/submittalChecklists.js'
import { getProjectDetails } from '../services/projects.js'
import { acquireLock, heartbeatLock, releaseLock } from '../services/locks.js'
import {
  getSubmittalReviewState, seedSubmittalChecklist, setSubmittalChecklistItemStatus,
  setSubmittalChecklistItemNote, addSubmittalChecklistNote, deleteSubmittalChecklistItem,
} from '../services/submittalReview.js'
import { ctx } from '../outline/ctx.js'
import { DISCIPLINE_ORDER, getDiscipline } from './DrawingSheetList.jsx'
import PdfPane from './pdfpane/PdfPane.jsx'
import AnnotatablePdfPane from './pdfpane/AnnotatablePdfPane.jsx'
import ResizeHandle from './ResizeHandle.jsx'
import { confirmDialog, promptDialog, alertDialog } from './Dialog.jsx'

const CHECKLIST_STATUS_ORDER = ['pending', 'pass', 'flagged', 'na']
const CHECKLIST_STATUS_STYLE = {
  pending: { label: 'Pending', color: 'var(--aj-text-muted)' },
  pass:    { label: 'Pass', color: '#1D9E75' },
  flagged: { label: 'Flagged', color: '#c0392b' },
  na:      { label: 'N/A', color: 'var(--aj-text-muted)' },
}
const HEARTBEAT_MS = 4 * 60 * 1000

const Z = 750
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
function fmtDate(d) {
  if (!d) return ''
  const p = String(d).slice(0, 10).split('-')
  if (p.length === 3) return MONTHS[parseInt(p[1], 10) - 1] + ' ' + parseInt(p[2], 10) + ', ' + p[0]
  return String(d)
}

/** Build the round picker options: a virtual "v1" round when the lazy rev-0 row doesn't exist
 *  yet, plus one entry per real submittal_revisions row — mirrors SubmissionNode's own
 *  Initial Submission (v1) / Resubmittal (vN) labeling exactly (ConstructionSection.jsx). */
function buildRoundOptions(revisions) {
  const sorted = Array.isArray(revisions) ? [...revisions].sort((a, b) => (a.revision_number || 0) - (b.revision_number || 0)) : []
  const opts = []
  if (!sorted.some(r => r.revision_number === 0)) {
    opts.push({ key: 'v1', revisionId: null, versionNo: 1, label: 'Initial Submission (v1)' })
  }
  for (const r of sorted) {
    const versionNo = (r.revision_number || 0) + 1
    opts.push({
      key: r.id, revisionId: r.id, versionNo,
      label: (r.revision_number === 0 ? 'Initial Submission' : 'Resubmittal') + ' (v' + versionNo + ')',
      revision: r,
    })
  }
  return opts
}

function matchesAnyPrefix(sectionNumbers, prefixes) {
  if (!prefixes || !prefixes.length) return false
  return sectionNumbers.some(num => {
    const n = String(num || '').trim()
    return n && prefixes.some(p => n.startsWith(String(p || '').trim()) && String(p || '').trim())
  })
}

export default function SubmittalReviewModal() {
  const [open, setOpen] = useState(false)
  const [submittalId, setSubmittalId] = useState(null)
  const [submittal, setSubmittal] = useState(null)
  const [revisions, setRevisions] = useState(null)   // null = loading, [] = loaded empty
  const [selectedRoundKey, setSelectedRoundKey] = useState(null)
  const [loadError, setLoadError] = useState(null)

  // Package pane
  const [packageFiles, setPackageFiles] = useState(null)   // null = loading, [] = loaded empty
  const [activeFileIdx, setActiveFileIdx] = useState(0)

  // Drawings pane — flex:1 (equal thirds with Package/Specs, see S.sideFlexPane), no manual width.
  const [drawings, setDrawings] = useState(null)           // null = loading, [] = loaded empty
  const [drawingsCollapsed, setDrawingsCollapsed] = useState(false)
  const [openDiscGroups, setOpenDiscGroups] = useState(() => new Set())
  const [selectedSheetId, setSelectedSheetId] = useState(null)

  // Specs pane — flex:1 (equal thirds with Package/Drawings, see S.sideFlexPane), no manual width.
  const [specs, setSpecs] = useState(null)                 // { mode, sections|pdf, ... } | null = loading
  const [specsCollapsed, setSpecsCollapsed] = useState(false)
  const [selectedSectionId, setSelectedSectionId] = useState(null)

  // Checklist strip
  const [templates, setTemplates] = useState(null)         // null = loading, [] = loaded empty
  const [selectedTemplateIds, setSelectedTemplateIds] = useState(() => new Set())
  const [checklistW, setChecklistW] = useState(() => { const v = parseInt(localStorage.getItem('subrev:checklistW') || '', 10); return v >= 260 && v <= 560 ? v : 340 })

  // Phase 5 — markup + checklist runtime state for the currently-resolved round.
  // { markup: {fileId: annotationJson}, checklist: [stateRow,...] } | null = loading
  const [reviewState, setReviewState] = useState(null)
  // Shared edit lock (Decision D10) on the LATEST round while it has a real revision id.
  const [lockHeld, setLockHeld] = useState(true)     // true = held by me / not applicable yet
  const [lockHolder, setLockHolder] = useState(null)
  const [projectInfo, setProjectInfo] = useState({ name: '', number: '' })
  const [checklistBusy, setChecklistBusy] = useState(false)

  const specAutoSelectedRef = useRef(false)
  const templateAutoSelectedRef = useRef(false)
  const ensuringRevisionRef = useRef(false)
  const lockedRevisionIdRef = useRef(null)
  const heartbeatTimerRef = useRef(null)
  // The revisionId the caller asked to open (if any) — consumed once by the round-resolving
  // effect below, kept separate from selectedRoundKey so "no preference" (null) is never
  // confused with "the virtual v1 round" (whose own revisionId is also null).
  const requestedRevisionIdRef = useRef(null)

  function releaseCurrentLock() {
    if (heartbeatTimerRef.current) { clearInterval(heartbeatTimerRef.current); heartbeatTimerRef.current = null }
    const revId = lockedRevisionIdRef.current
    lockedRevisionIdRef.current = null
    if (revId) releaseLock('submittal_review', revId).catch(() => {})
  }

  // ── Open ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    function onOpen(e) {
      const d = e.detail || {}
      if (!d.submittalId) return
      setSubmittalId(d.submittalId)
      setSubmittal(null)
      setRevisions(null)
      setLoadError(null)
      setPackageFiles(null)
      setActiveFileIdx(0)
      setDrawings(null)
      setSelectedSheetId(null)
      setSpecs(null)
      setSelectedSectionId(null)
      setTemplates(null)
      setSelectedTemplateIds(new Set())
      setReviewState(null)
      setLockHeld(true)
      setLockHolder(null)
      setProjectInfo({ name: '', number: '' })
      specAutoSelectedRef.current = false
      templateAutoSelectedRef.current = false
      ensuringRevisionRef.current = false
      releaseCurrentLock()
      requestedRevisionIdRef.current = d.revisionId || null
      // Not yet resolved to a concrete option key — the round-resolving effect below picks the
      // requested round (if it exists once revisions load) or defaults to the latest round.
      setSelectedRoundKey(null)
      setOpen(true)
    }
    window.addEventListener('open-submittal-review', onOpen)
    return () => window.removeEventListener('open-submittal-review', onOpen)
  }, [])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') setOpen(false) }
    if (open) document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  // Load the submittal + its revision chain.
  useEffect(() => {
    if (!open || !submittalId) return
    let cancelled = false
    Promise.all([getSubmittal(submittalId), getSubmittalRevisions(submittalId)])
      .then(([s, revs]) => {
        if (cancelled) return
        if (!s) { setLoadError('Submittal not found.'); return }
        setSubmittal(s)
        setRevisions(revs || [])
      })
      .catch(e => { if (!cancelled) setLoadError(e.message || String(e)) })
    return () => { cancelled = true }
  }, [open, submittalId])

  const roundOptions = useMemo(() => buildRoundOptions(revisions), [revisions])

  // Resolve the requested/default round once options are known. Runs again whenever
  // selectedRoundKey changes, but no-ops as soon as it names a currently-valid option — so a
  // user's own round-switcher selection is never fought.
  useEffect(() => {
    if (!roundOptions.length) return
    if (selectedRoundKey != null && roundOptions.some(o => o.key === selectedRoundKey)) return
    const wanted = requestedRevisionIdRef.current
    const match = wanted ? roundOptions.find(o => o.revisionId === wanted) : null
    setSelectedRoundKey((match || roundOptions[roundOptions.length - 1]).key)
  }, [roundOptions, selectedRoundKey])

  const selectedRound = roundOptions.find(o => o.key === selectedRoundKey) || roundOptions[roundOptions.length - 1] || null
  const isLatestRound = !selectedRound || (roundOptions.length && selectedRound.key === roundOptions[roundOptions.length - 1].key)
  // A round is POSTED (frozen for markup/checklist edits) exactly when ConstructionSection.jsx's
  // own ReviewNode/SubmissionNode compute isPosted/reviewPosted — kept in lockstep with the
  // server-side freeze guard in api/submittal-review.js#isRevisionPosted.
  const isRoundPosted = !!(selectedRound && selectedRound.revision && selectedRound.revision.is_draft === false &&
    Array.isArray(selectedRound.revision.disposition) && selectedRound.revision.disposition.length > 0)

  // Lazily create the round's revision row (Rev 0) the moment the tool is opened on the LATEST
  // round and no real revision exists yet ("virtual v1"). Mirrors LogSubmittalReviewModal's own
  // lazy rev-0 creation, but fires proactively on open rather than waiting for a save — a review
  // is IN PROGRESS the moment markup/checklist work starts, and that has to happen before a
  // disposition can be logged, so waiting for "Log Review" to create the row would make it
  // impossible to ever start marking up a fresh submittal. This mirrors QcMarkupModal's own
  // bootstrap precedent (createOrGetVersion is called unconditionally on every open). The created
  // row is a content-free draft (is_draft: true, no dates/disposition) — ConstructionSection.jsx's
  // own `isDraft`/`reviewExists` checks require actual content (dispList.length or a date), so this
  // stays invisible in the outline until the reviewer actually logs something.
  useEffect(() => {
    if (!open || !submittal || !isLatestRound || !selectedRound) return
    if (selectedRound.revisionId != null) return   // already a real round
    if (ensuringRevisionRef.current) return
    ensuringRevisionRef.current = true
    createSubmittalRevision(submittal.id, { is_draft: true }).then(rev => {
      ensuringRevisionRef.current = false
      if (!rev) return
      setRevisions(prev => [...(Array.isArray(prev) ? prev : []), rev])
      requestedRevisionIdRef.current = rev.id
      setSelectedRoundKey(rev.id)
    }).catch(() => { ensuringRevisionRef.current = false })
  }, [open, submittal, isLatestRound, selectedRound])

  // Shared edit lock (Decision D10): held while the LATEST round is open AND has a real revision
  // id. Viewing a past round never needs it (Phase 4's isLatestRound already forces read-only).
  useEffect(() => {
    const revId = (open && isLatestRound && selectedRound && selectedRound.revisionId) ? selectedRound.revisionId : null
    if (!revId) {
      if (lockedRevisionIdRef.current) releaseCurrentLock()
      setLockHeld(true); setLockHolder(null)
      return
    }
    if (lockedRevisionIdRef.current === revId) return
    if (lockedRevisionIdRef.current) releaseCurrentLock()
    let cancelled = false
    const name = ctx.currentMember?.fields?.['Member Name'] || ''
    acquireLock('submittal_review', revId, name).then(lock => {
      if (cancelled) return
      if (lock && lock.locked === false) {
        setLockHeld(false); setLockHolder(lock.heldBy || null)
      } else {
        lockedRevisionIdRef.current = revId
        setLockHeld(true); setLockHolder(null)
        if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current)
        heartbeatTimerRef.current = setInterval(async () => {
          try {
            const r = await heartbeatLock('submittal_review', revId)
            if (!r.locked) { setLockHeld(false); setLockHolder(r.heldBy || null) }
          } catch { /* transient — next heartbeat retries */ }
        }, HEARTBEAT_MS)
      }
    }).catch(() => { if (!cancelled) { setLockHeld(true); setLockHolder(null) } })
    return () => { cancelled = true }
  }, [open, isLatestRound, selectedRound])

  // Release the lock whenever this (globally-mounted) modal is hidden.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!open) releaseCurrentLock() }, [open])

  // Editable = the latest round, with a real revision id, whose lock we hold, and whose review
  // hasn't been posted yet. Drives both the package pane's markup layer and the checklist strip.
  const canEdit = !!(open && isLatestRound && selectedRound && selectedRound.revisionId && lockHeld && !isRoundPosted)

  // Markup + checklist state for the resolved round (works for read-only past rounds too — only
  // WRITES are freeze/lock-guarded, reads are always allowed).
  useEffect(() => {
    if (!open || !selectedRound || !selectedRound.revisionId) { setReviewState({ markup: {}, checklist: [] }); return }
    let cancelled = false
    setReviewState(null)
    getSubmittalReviewState(selectedRound.revisionId)
      .then(d => { if (!cancelled) setReviewState(d || { markup: {}, checklist: [] }) })
      .catch(() => { if (!cancelled) setReviewState({ markup: {}, checklist: [] }) })
    return () => { cancelled = true }
  }, [open, selectedRound])

  // Project name/number for the "Log Review" handoff (LogSubmittalReviewModal's transmittal header).
  useEffect(() => {
    if (!open || !submittal || !submittal.project_id) return
    let cancelled = false
    getProjectDetails(submittal.project_id).then(p => {
      if (cancelled || !p) return
      setProjectInfo({ name: p.fields?.['Project Name'] || '', number: p.fields?.['Project Number'] || '' })
    }).catch(() => {})
    return () => { cancelled = true }
  }, [open, submittal])

  // Package files for the selected round (round 1 = submittal-level files; later rounds = the
  // revision's own 'package' files — mirrors SubmissionNode's own loader in ConstructionSection.jsx).
  useEffect(() => {
    if (!open || !submittal || !selectedRound) return
    let cancelled = false
    setPackageFiles(null)
    setActiveFileIdx(0)
    const loader = selectedRound.versionNo === 1
      ? getSubmittalFiles(submittal.id)
      : getRevisionFiles(selectedRound.revisionId).then(fs => (fs || []).filter(f => f.kind !== 'review'))
    loader.then(fs => { if (!cancelled) setPackageFiles(fs || []) }).catch(() => { if (!cancelled) setPackageFiles([]) })
    return () => { cancelled = true }
  }, [open, submittal, selectedRound])

  // Drawings + specs + checklist templates load once per open (project-scoped, round-independent).
  useEffect(() => {
    if (!open || !submittal) return
    const projectId = submittal.project_id
    let cancelled = false
    if (projectId) {
      getCurrentDrawingSet(projectId).then(d => { if (!cancelled) setDrawings(d || []) }).catch(() => { if (!cancelled) setDrawings([]) })
      getReviewSpecs(projectId).then(d => { if (!cancelled) setSpecs(d || { mode: 'legacy', pdf: null }) }).catch(() => { if (!cancelled) setSpecs({ mode: 'legacy', pdf: null }) })
    } else {
      setDrawings([])
      setSpecs({ mode: 'legacy', pdf: null })
    }
    getSubmittalChecklistTemplates().then(t => { if (!cancelled) setTemplates(t || []) }).catch(() => { if (!cancelled) setTemplates([]) })
    return () => { cancelled = true }
  }, [open, submittal])

  // The submittal's own linked section numbers (primary first) — pin to top of the specs list and
  // drive checklist auto-suggest (Decision D5).
  const linkedSectionNumbers = useMemo(() => {
    const secs = (submittal && submittal.sections) || []
    return secs.map(s => s.section_number).filter(Boolean)
  }, [submittal])

  // Specs: auto-pin + auto-select the submittal's own section (sections mode only), once.
  const orderedSpecSections = useMemo(() => {
    if (!specs || specs.mode !== 'sections') return []
    const linkedSet = new Set(linkedSectionNumbers)
    const linked = specs.sections.filter(s => linkedSet.has(s.sectionNumber))
    const rest = specs.sections.filter(s => !linkedSet.has(s.sectionNumber))
    return [...linked, ...rest]
  }, [specs, linkedSectionNumbers])

  useEffect(() => {
    if (specAutoSelectedRef.current) return
    if (!specs || specs.mode !== 'sections' || !orderedSpecSections.length) return
    specAutoSelectedRef.current = true
    setSelectedSectionId(orderedSpecSections[0].id)
  }, [specs, orderedSpecSections])

  // Checklist: auto-suggest templates whose sectionPrefixes match the submittal's sections.
  useEffect(() => {
    if (templateAutoSelectedRef.current) return
    if (!templates || !templates.length) return
    templateAutoSelectedRef.current = true
    const suggested = templates.filter(t => matchesAnyPrefix(linkedSectionNumbers, t.sectionPrefixes)).map(t => t.id)
    if (suggested.length) setSelectedTemplateIds(new Set(suggested))
  }, [templates, linkedSectionNumbers])

  function toggleTemplate(id) {
    setSelectedTemplateIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  function close() { releaseCurrentLock(); setOpen(false) }

  // ── Checklist runtime (Phase 5) ────────────────────────────────────────────────
  const memberId = ctx.currentMemberId || ''
  const memberName = ctx.currentMember?.fields?.['Member Name'] || ''

  async function handleStartChecklist() {
    if (!selectedRound || !selectedRound.revisionId || checklistBusy) return
    setChecklistBusy(true)
    try {
      const checklist = await seedSubmittalChecklist(selectedRound.revisionId, [...selectedTemplateIds])
      setReviewState(prev => ({ markup: (prev && prev.markup) || {}, checklist }))
    } catch (e) {
      await alertDialog({ title: 'Could not start checklist', message: e.message || String(e) })
    } finally { setChecklistBusy(false) }
  }
  async function cycleChecklistStatus(item) {
    if (!canEdit) return
    const next = CHECKLIST_STATUS_ORDER[(CHECKLIST_STATUS_ORDER.indexOf(item.status) + 1) % CHECKLIST_STATUS_ORDER.length]
    try {
      const updated = await setSubmittalChecklistItemStatus(item.id, next, memberId, memberName)
      setReviewState(prev => ({ ...prev, checklist: prev.checklist.map(x => x.id === item.id ? updated : x) }))
    } catch (e) {
      if (e && e.status !== 409) await alertDialog({ title: 'Could not update item', message: e.message || String(e) })
    }
  }
  async function saveChecklistNote(item, note) {
    if (!canEdit) return
    try {
      const updated = await setSubmittalChecklistItemNote(item.id, note)
      setReviewState(prev => ({ ...prev, checklist: prev.checklist.map(x => x.id === item.id ? updated : x) }))
    } catch { /* best-effort — the textarea keeps the typed value regardless */ }
  }
  async function handleAddChecklistNote() {
    if (!selectedRound || !selectedRound.revisionId) return
    const text = await promptDialog({ title: 'Add checklist note', placeholder: 'Note text', multiline: true })
    if (text === null || !text.trim()) return
    try {
      const item = await addSubmittalChecklistNote(selectedRound.revisionId, text.trim(), memberId, memberName)
      setReviewState(prev => ({ ...prev, checklist: [...prev.checklist, item] }))
    } catch (e) {
      await alertDialog({ title: 'Could not add note', message: e.message || String(e) })
    }
  }
  async function handleDeleteChecklistNote(item) {
    if (!(await confirmDialog({ title: 'Delete note?', message: 'Delete this note?', confirmLabel: 'Delete', danger: true }))) return
    try {
      await deleteSubmittalChecklistItem(item.id)
      setReviewState(prev => ({ ...prev, checklist: prev.checklist.filter(x => x.id !== item.id) }))
    } catch (e) {
      await alertDialog({ title: 'Could not delete note', message: e.message || String(e) })
    }
  }
  function openLogReview() {
    if (!submittal || !selectedRound) return
    const rev = selectedRound.revision || null
    window.dispatchEvent(new CustomEvent('open-log-submittal-review', {
      detail: {
        submittal, revisionId: selectedRound.revisionId,
        revisionNumber: rev ? rev.revision_number : 0,
        dateReceived: (rev && rev.date_received) || submittal.date_received || '',
        projectName: projectInfo.name, projectNumber: projectInfo.number,
        review: rev ? {
          disposition: rev.disposition, review_comments: rev.review_comments,
          date_sent: rev.date_sent, date_received_back: rev.date_received_back, date_returned: rev.date_returned,
        } : null,
      },
    }))
  }

  // Drawings grouped by discipline, ordered per the firm's canonical sequence (DISCIPLINE_ORDER).
  // Must stay above the `if (!open) return null` below — a hook can never be called
  // conditionally (React error #310, "rendered fewer hooks than expected" on the render where
  // the modal first opens, since this useMemo would be skipped whenever `open` is false).
  const drawingGroups = useMemo(() => {
    if (!Array.isArray(drawings)) return []
    const groups = {}
    for (const d of drawings) {
      const disc = d.discipline || getDiscipline(d.title || d.number || '') || 'Other'
      ;(groups[disc] = groups[disc] || []).push(d)
    }
    const orderOf = disc => { const i = DISCIPLINE_ORDER.indexOf(disc); return i === -1 ? 999 : i }
    return Object.keys(groups).sort((a, b) => orderOf(a) - orderOf(b) || a.localeCompare(b)).map(disc => ({ discipline: disc, rows: groups[disc] }))
  }, [drawings])

  if (!open) return null

  const title = submittal ? submittalLabel(submittal, submittal.sections) : 'Loading…'

  function toggleDiscGroup(disc) {
    setOpenDiscGroups(prev => { const n = new Set(prev); if (n.has(disc)) n.delete(disc); else n.add(disc); return n })
  }

  const activePackagePdfUrl = Array.isArray(packageFiles) && packageFiles[activeFileIdx] ? packageFiles[activeFileIdx].url : null
  const selectedSheet = Array.isArray(drawings) ? drawings.find(d => d.id === selectedSheetId) : null
  const selectedSection = specs && specs.mode === 'sections' ? orderedSpecSections.find(s => s.id === selectedSectionId) : null

  return createPortal(
    <div data-modal-id="SubmittalReviewModal" style={S.root}>
      {/* Top bar */}
      <div style={S.topBar}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <span style={S.title} title={title}>{title}</span>
          {selectedRound && roundOptions.length > 0 && (
            <>
              <select value={selectedRound.key} onChange={e => setSelectedRoundKey(e.target.value)} style={S.roundSelect} title="View a different submission round">
                {roundOptions.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
              <span style={S.roundBadge}>v{selectedRound.versionNo} of {roundOptions.length}</span>
            </>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {isLatestRound && selectedRound && (
            <button onClick={openLogReview} style={S.logReviewBtn}>
              {isRoundPosted ? 'Edit Review' : 'Log Review'}
            </button>
          )}
          <button onClick={close} style={S.closeBtn} title="Close (Esc)">×</button>
        </div>
      </div>

      {loadError && <div style={S.errorBanner}>{loadError}</div>}
      {!isLatestRound && !loadError && (
        <div style={S.readOnlyBanner}>
          Read-only — viewing round {selectedRound.versionNo} of {roundOptions.length}. Switch back to the latest round above to see the current state.
        </div>
      )}
      {isLatestRound && !lockHeld && (
        <div style={S.lockBanner}>
          {lockHolder && lockHolder.name ? lockHolder.name + ' has this review open — read-only.' : 'Another session has this review open — read-only.'}
        </div>
      )}
      {isLatestRound && lockHeld && isRoundPosted && (
        <div style={S.readOnlyBanner}>This round's review has been posted — markup and checklist are locked. Log a resubmittal to start a new round.</div>
      )}

      {/* Body */}
      <div style={S.body}>
        {/* Package pane */}
        <div style={S.mainPaneWrap}>
          <AnnotatablePdfPane
            // Keyed on round + file + whether reviewState has resolved — remounts (fresh
            // useRasterViewport + a correctly-seeded initialAnnotation) whenever any of those
            // change, so a round/file switch never shows stale markup or starts from an empty
            // annotation because reviewState hadn't loaded yet at mount time.
            key={(selectedRound ? selectedRound.revisionId || selectedRound.key : 'none') + ':' +
              (packageFiles && packageFiles[activeFileIdx] ? packageFiles[activeFileIdx].id : 'nofile') + ':' +
              (reviewState ? 'rs' : 'loading')}
            pdfUrl={activePackagePdfUrl}
            title="Submitted Package"
            revisionId={selectedRound ? selectedRound.revisionId : null}
            fileId={packageFiles && packageFiles[activeFileIdx] ? packageFiles[activeFileIdx].id : null}
            initialAnnotation={reviewState && packageFiles && packageFiles[activeFileIdx] ? (reviewState.markup[packageFiles[activeFileIdx].id] || null) : null}
            editable={canEdit && !!(packageFiles && packageFiles[activeFileIdx])}
            authorName={memberName}
            extraToolbarContent={
              packageFiles === null
                ? <span style={S.paneHint}>Loading files…</span>
                : packageFiles.length === 0
                  ? <span style={S.paneHint}>No package files on this round.</span>
                  : packageFiles.length > 1
                    ? (
                      <select value={activeFileIdx} onChange={e => setActiveFileIdx(Number(e.target.value))} style={S.fileSelect}>
                        {packageFiles.map((f, i) => <option key={f.id} value={i}>{f.filename}</option>)}
                      </select>
                    )
                    : <span style={S.paneHint}>{packageFiles[0].filename}</span>
            }
          />
        </div>

        {/* Drawings pane */}
        {drawingsCollapsed ? (
          <CollapsedRail label="Drawings" onExpand={() => setDrawingsCollapsed(false)} />
        ) : (
          <>
            <div style={S.sideFlexPane}>
              <div style={S.paneHeader}>
                <span style={S.paneHeaderTitle}>Drawings</span>
                <button onClick={() => setDrawingsCollapsed(true)} style={S.chevronBtn} title="Collapse">›</button>
              </div>
              <div style={S.paneList}>
                {drawings === null && <div style={S.paneEmpty}>Loading drawings…</div>}
                {Array.isArray(drawings) && drawings.length === 0 && <div style={S.paneEmpty}>No current drawing set.</div>}
                {drawingGroups.map(g => {
                  const groupOpen = openDiscGroups.has(g.discipline)
                  return (
                    <div key={g.discipline}>
                      <button onClick={() => toggleDiscGroup(g.discipline)} style={S.discHeader}>
                        <span style={{ width: 10, display: 'inline-block' }}>{groupOpen ? '▾' : '▸'}</span>
                        {g.discipline}
                        <span style={{ marginLeft: 'auto', color: 'var(--aj-text-muted)' }}>{g.rows.length}</span>
                      </button>
                      {groupOpen && g.rows.map(d => (
                        <button key={d.id} onClick={() => setSelectedSheetId(d.id)} disabled={d.id === selectedSheetId} style={S.sheetRow(d.id === selectedSheetId)}>
                          <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.number} {d.title}</span>
                          {!d.pdfUrl && <span style={S.noPdfTag}>No PDF</span>}
                        </button>
                      ))}
                    </div>
                  )
                })}
              </div>
              <div style={S.subPaneWrap}>
                <PdfPane pdfUrl={selectedSheet ? selectedSheet.pdfUrl : null} title={selectedSheet ? (selectedSheet.number + ' ' + selectedSheet.title) : null} />
              </div>
            </div>
          </>
        )}

        {/* Specs pane */}
        {specsCollapsed ? (
          <CollapsedRail label="Specs" onExpand={() => setSpecsCollapsed(false)} />
        ) : (
          <>
            <div style={S.sideFlexPane}>
              <div style={S.paneHeader}>
                <span style={S.paneHeaderTitle}>Specs</span>
                <button onClick={() => setSpecsCollapsed(true)} style={S.chevronBtn} title="Collapse">›</button>
              </div>
              {specs === null && <div style={S.paneEmpty}>Loading specs…</div>}
              {specs && specs.mode === 'sections' && (
                <>
                  <div style={S.paneList}>
                    {orderedSpecSections.map(s => {
                      const linked = linkedSectionNumbers.includes(s.sectionNumber)
                      return (
                        <button key={s.id} onClick={() => setSelectedSectionId(s.id)} disabled={s.id === selectedSectionId} style={S.sheetRow(s.id === selectedSectionId)}>
                          <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {s.sectionNumber} {s.sectionName}{linked ? ' ★' : ''}
                          </span>
                          {!s.latestVersion?.pdfUrl && <span style={S.noPdfTag}>No PDF</span>}
                        </button>
                      )
                    })}
                    {!orderedSpecSections.length && <div style={S.paneEmpty}>No issued spec sections.</div>}
                  </div>
                  <div style={S.subPaneWrap}>
                    <PdfPane pdfUrl={selectedSection ? selectedSection.latestVersion?.pdfUrl : null} title={selectedSection ? (selectedSection.sectionNumber + ' ' + selectedSection.sectionName) : null} />
                  </div>
                </>
              )}
              {specs && specs.mode === 'legacy' && (
                <>
                  <div style={S.subPaneWrap}>
                    <PdfPane pdfUrl={specs.pdf ? specs.pdf.url : null} title={specs.pdf ? 'Specifications v' + specs.versionNumber : null} />
                  </div>
                  <div style={S.legacyHistWrap}>
                    <div style={S.paneHeaderTitle}>Version history</div>
                    {!specs.history || !specs.history.length ? (
                      <div style={S.paneEmpty}>No posted spec versions for this project.</div>
                    ) : specs.history.map(v => (
                      <div key={v.version} style={S.legacyHistRow}>
                        <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 11, color: 'var(--aj-text)' }}>v{v.version}</span>
                        {v.postDate && <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-muted)' }}>{fmtDate(v.postDate)}</span>}
                        {v.pdf && <a href={v.pdf.url} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 'auto', fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-accent-blue,#1a6fa8)' }}>View ↗</a>}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {/* Checklist strip — interactive on the latest, editable round (Phase 5); a read-only
            preview of the seeded state on past rounds or when the lock is held elsewhere. */}
        <>
          <ResizeHandle dir={-1} getStart={() => checklistW} min={260} max={560}
            onChange={setChecklistW} onCommit={w => { try { localStorage.setItem('subrev:checklistW', String(w)) } catch {} }} />
          <div style={{ ...S.sidePane, width: checklistW }}>
            <div style={S.paneHeader}>
              <span style={S.paneHeaderTitle}>Review Checklist</span>
            </div>
            <div style={S.checklistBody}>
              {(reviewState === null || templates === null) && <div style={S.paneEmpty}>Loading checklist…</div>}

              {reviewState && templates && reviewState.checklist.length === 0 && (
                <>
                  {Array.isArray(templates) && templates.length === 0 && <div style={S.paneEmpty}>No checklist templates yet — add one in Templates → Submittal.</div>}
                  {Array.isArray(templates) && templates.length > 0 && (
                    <div style={S.templatePicker}>
                      {templates.map(t => (
                        <label key={t.id} style={S.templateCheckRow}>
                          <input type="checkbox" disabled={!canEdit} checked={selectedTemplateIds.has(t.id)} onChange={() => toggleTemplate(t.id)} />
                          <span style={{ flex: 1 }}>{t.name}</span>
                          <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-muted)' }}>{t.items.length}</span>
                        </label>
                      ))}
                      {canEdit ? (
                        <button onClick={handleStartChecklist} disabled={checklistBusy || !selectedTemplateIds.size} style={S.startChecklistBtn}>
                          {checklistBusy ? 'Starting…' : 'Start checklist'}
                        </button>
                      ) : (
                        <div style={{ ...S.paneEmpty, padding: '8px 0 0' }}>
                          {isRoundPosted ? 'This round is posted — no checklist was started.' : !lockHeld ? 'Read-only — someone else has this review open.' : 'Read-only — viewing a past round.'}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {reviewState && reviewState.checklist.length > 0 && (
                <div style={S.checklistPreview}>
                  {groupChecklistByTemplate(reviewState.checklist, templates).map(g => (
                    <div key={g.key} style={{ marginBottom: 14 }}>
                      <div style={S.checklistTplHeading}>{g.name}</div>
                      {g.items.map(it => (
                        <ChecklistItemRow key={it.id} item={it} editable={canEdit}
                          onCycle={() => cycleChecklistStatus(it)}
                          onSaveNote={note => saveChecklistNote(it, note)}
                          onDelete={it.templateItemId ? null : () => handleDeleteChecklistNote(it)} />
                      ))}
                    </div>
                  ))}
                  {canEdit && (
                    <button onClick={handleAddChecklistNote} style={S.addNoteBtn}>+ Add note</button>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      </div>
    </div>,
    document.body
  )
}

/** Group checklist state rows by templateId, preserving first-seen order (already sort_order-
 *  ordered by the API). Free-note rows (template_id null) collect into a trailing "Notes" group.
 *  `templates` (the already-loaded template list) resolves a group's display name — the state
 *  rows themselves only carry the template's id, not its name. */
function groupChecklistByTemplate(items, templates) {
  const nameFor = id => (Array.isArray(templates) && templates.find(t => t.id === id)?.name) || 'Checklist'
  const order = []
  const byKey = {}
  for (const it of items) {
    const key = it.templateId || '__notes__'
    if (!byKey[key]) { byKey[key] = { key, name: it.templateId ? nameFor(it.templateId) : 'Notes', items: [] }; order.push(key) }
    byKey[key].items.push(it)
  }
  return order.map(k => byKey[k])
}

function ChecklistItemRow({ item, editable, onCycle, onSaveNote, onDelete }) {
  const [note, setNote] = useState(item.note || '')
  useEffect(() => { setNote(item.note || '') }, [item.id, item.note])
  const style = CHECKLIST_STATUS_STYLE[item.status] || CHECKLIST_STATUS_STYLE.pending
  return (
    <div style={S.clItemRow}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <button onClick={onCycle} disabled={!editable} title={editable ? 'Click to change status' : undefined}
          style={{ ...S.clStatusPill, color: style.color, borderColor: style.color + '55', cursor: editable ? 'pointer' : 'default' }}>
          {style.label}
        </button>
        <span style={{ flex: 1, fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text)' }}>{item.itemText}</span>
        {item.carriedFromRevisionId && <span style={S.carriedBadge} title="Flagged in a previous round">↩ previously flagged</span>}
        {onDelete && editable && <button onClick={onDelete} style={S.clDeleteBtn} title="Delete note">×</button>}
      </div>
      {item.status === 'flagged' && (
        <textarea
          value={note}
          disabled={!editable}
          onChange={e => setNote(e.target.value)}
          onBlur={() => { if (editable && note !== (item.note || '')) onSaveNote(note) }}
          placeholder="Note (why this was flagged)…"
          style={S.clNoteArea}
        />
      )}
    </div>
  )
}

function CollapsedRail({ label, onExpand }) {
  return (
    <div style={S.rail}>
      <button onClick={onExpand} title={'Show ' + label} style={S.railBtn}>‹</button>
      <span style={S.railLabel}>{label}</span>
    </div>
  )
}

const S = {
  root: { position: 'fixed', inset: 0, zIndex: Z, background: 'var(--aj-bg-page)', display: 'flex', flexDirection: 'column' },
  topBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
    padding: '10px 16px', background: 'var(--aj-bg-elevated)', borderBottom: '0.5px solid var(--aj-border)', flexShrink: 0,
  },
  title: {
    fontFamily: 'Montserrat,sans-serif', fontWeight: 700, fontSize: 13, letterSpacing: '0.02em',
    color: 'var(--aj-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 420,
  },
  roundSelect: {
    fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', background: 'var(--aj-bg-surface)',
    border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '3px 6px', maxWidth: 220,
  },
  roundBadge: { fontFamily: '"DM Mono",monospace', fontSize: 11, color: 'var(--aj-text-muted)', flexShrink: 0 },
  closeBtn: { background: 'none', border: 'none', color: 'var(--aj-text-mid)', fontSize: 22, lineHeight: 1, cursor: 'pointer', flexShrink: 0 },
  errorBanner: { padding: '6px 16px', background: '#3a1414', color: '#f3a3a3', fontFamily: '"DM Sans",sans-serif', fontSize: 12, flexShrink: 0 },
  readOnlyBanner: {
    padding: '6px 16px', background: 'var(--aj-bg-muted)', color: 'var(--aj-text-mid)',
    fontFamily: '"DM Sans",sans-serif', fontSize: 12, flexShrink: 0, borderBottom: '0.5px solid var(--aj-border)',
  },
  body: { flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' },
  mainPaneWrap: { flex: 1, minWidth: 0, minHeight: 0, display: 'flex' },
  sidePane: { flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0, background: 'var(--aj-bg-surface)' },
  // Drawings/Specs — equal-thirds flexible column (with Package's flex:1 mainPaneWrap), no
  // manual width — see Matt's 2026-08 feedback: split the three left panes equally, keep only
  // the Checklist strip (S.sidePane) as a manually-resized, fixed-width sidebar.
  sideFlexPane: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0, background: 'var(--aj-bg-surface)', borderLeft: '0.5px solid var(--aj-border)' },
  subPaneWrap: { flex: 1, minHeight: 0, display: 'flex' },
  paneHeader: {
    display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px',
    borderBottom: '0.5px solid var(--aj-border)', background: 'var(--aj-bg-elevated)', flexShrink: 0,
  },
  paneHeaderTitle: { fontFamily: '"DM Mono",monospace', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--aj-text-mid)', flex: 1 },
  chevronBtn: { background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, color: 'var(--aj-text-mid)', fontSize: 13, width: 20, height: 20, cursor: 'pointer', lineHeight: 1 },
  // Fixed pixel cap, not a percentage — a percentage maxHeight here depends on this flex
  // column's own height being "definite," which is unreliable through this many nested flex
  // levels (body row > sideFlexPane column > this) and left the PdfPane below it with an
  // unpredictably small remainder in practice (2026-08 fix).
  paneList: { maxHeight: 260, overflowY: 'auto', borderBottom: '0.5px solid var(--aj-border)', flexShrink: 0 },
  paneEmpty: { padding: '10px 12px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-muted)' },
  paneHint: { fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 },
  fileSelect: { fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', background: 'var(--aj-bg-surface)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '3px 6px', maxWidth: 220 },
  discHeader: {
    display: 'flex', width: '100%', alignItems: 'center', gap: 6, padding: '6px 10px', border: 'none',
    borderBottom: '0.5px solid var(--aj-border)', background: 'var(--aj-bg-subtle)', cursor: 'pointer',
    fontFamily: '"DM Mono",monospace', fontSize: 10.5, color: 'var(--aj-text)', textTransform: 'uppercase', letterSpacing: '0.04em',
  },
  sheetRow: (active) => ({
    display: 'flex', alignItems: 'center', gap: 6, width: '100%', textAlign: 'left', padding: '6px 10px 6px 18px',
    border: 'none', borderBottom: '0.5px solid var(--aj-border)', background: active ? 'var(--aj-bg-muted)' : 'var(--aj-bg-elevated)',
    color: 'var(--aj-text)', fontFamily: '"DM Sans",sans-serif', fontSize: 12, cursor: active ? 'default' : 'pointer',
  }),
  noPdfTag: { fontSize: 9.5, fontFamily: '"DM Mono",monospace', color: 'var(--aj-text-muted)', flexShrink: 0 },
  legacyHistWrap: { flex: '0 0 auto', maxHeight: 220, overflowY: 'auto', padding: '8px 10px', borderTop: '0.5px solid var(--aj-border)' },
  legacyHistRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '0.5px solid var(--aj-border)' },
  checklistBody: { flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' },
  templatePicker: { padding: '8px 10px', borderBottom: '0.5px solid var(--aj-border)' },
  templateCheckRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text)', cursor: 'pointer' },
  checklistPreview: { padding: '10px' },
  checklistTplHeading: { fontFamily: '"DM Mono",monospace', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--aj-text-mid)', marginBottom: 4 },
  checklistItemRow: { fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text)', padding: '4px 0', borderBottom: '0.5px solid var(--aj-border)' },
  rail: { width: 36, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 8, gap: 8, background: 'var(--aj-bg-elevated)', borderLeft: '0.5px solid var(--aj-border)' },
  railBtn: { background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, color: 'var(--aj-text-mid)', fontSize: 13, width: 22, height: 22, cursor: 'pointer', lineHeight: 1 },
  railLabel: { writingMode: 'vertical-rl', textOrientation: 'mixed', fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-muted)', letterSpacing: '0.04em' },
  logReviewBtn: {
    fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, padding: '5px 12px', borderRadius: 4,
    border: 'none', background: '#07272D', color: '#fff', cursor: 'pointer', flexShrink: 0,
  },
  lockBanner: {
    padding: '6px 16px', background: '#3a2a10', color: '#f0c890',
    fontFamily: '"DM Sans",sans-serif', fontSize: 12, flexShrink: 0, borderBottom: '0.5px solid var(--aj-border)',
  },
  startChecklistBtn: {
    marginTop: 8, width: '100%', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600,
    padding: '6px 10px', borderRadius: 4, border: 'none', background: '#07272D', color: '#fff', cursor: 'pointer',
  },
  addNoteBtn: {
    fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-accent-blue,#1a6fa8)',
    background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0', textAlign: 'left',
  },
  clItemRow: { padding: '6px 0', borderBottom: '0.5px solid var(--aj-border)' },
  clStatusPill: {
    fontFamily: '"DM Mono",monospace', fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
    border: '0.5px solid', background: 'none', flexShrink: 0,
  },
  carriedBadge: { fontSize: 10, fontFamily: '"DM Sans",sans-serif', color: '#c79a3a', flexShrink: 0, whiteSpace: 'nowrap' },
  clDeleteBtn: { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 14, lineHeight: 1, padding: '0 2px', flexShrink: 0 },
  clNoteArea: {
    width: '100%', marginTop: 5, padding: '5px 7px', fontFamily: '"DM Sans",sans-serif', fontSize: 11.5,
    color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)',
    borderRadius: 4, boxSizing: 'border-box', resize: 'vertical', minHeight: 40,
  },
}
